/**
 * Shared helpers for sending EFAS notification emails.
 *
 * Used both by the compose flow (which creates the SentNotification/AlertPoint
 * records and then emails them out) and by the resend flow in
 * `cart-notifications`, which replays ONLY the email step for a notification that
 * already exists - typically after an upstream SMTP failure. Nothing in here
 * writes to /api/notifications/, so it is safe for the resend case.
 */
import { toast } from 'react-toastify';

export const EFAS_FROM = "efas@ecmwf.int";
export const EFAS_SELFTEST_FROM = "efas-selftest@ecmwf.int";

export interface NotificationEmail {
    from: string,
    to: string,
    cc?: string,
    subject: string,
    body: string,
}

/** POST the email to the backend, which relays it through the ECMWF SMTP server. */
export function sendNotificationEmail(email: NotificationEmail) {
    return fetch('/api/email/send/', {
        body: JSON.stringify(email),
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }
    });
}

/**
 * The contact emails of the given partners, plus their (non-empty) distribution
 * lists. These are the default recipients of a notification.
 */
export async function fetchPartnerEmails(partnerIds: (string | number)[]): Promise<string[]> {
    const query = partnerIds.map((id) => `id=${id}`).join('&');

    const partnerEmails = await (await fetch(`/api/notifications/notification-emails/?format=json&${query}`)).json();
    const partnerDetails = await (await fetch(`/api/notifications/partners/?format=json&${query}`)).json();

    const emails = partnerEmails.map((item: { email: string }) => item.email);
    const distributionListEmails = partnerDetails
        .map((item: { distribution_list: string }) => item.distribution_list)
        .filter((email: string) => email.length !== 0);

    return [...emails, ...distributionListEmails];
}

/** The CC recipients configured on the notification's alert type. */
export async function fetchAlertTypeCC(notificationType: string | number): Promise<string[]> {
    const data: { cc_emails: string, name: string } =
        await (await fetch(`/api/notifications/alert-types/${notificationType}/?format=json`)).json();

    return data.cc_emails.split(",");
}

/** The point fields the subject is built from. Some of them are typed as arrays on
 *  the parsed data_summary and as plain strings on the cart points, hence the union. */
interface SubjectPoint {
    Country: string | string[],
    RiverName: string,
    ad2_name: string | string[],
}

const asText = (value: string | string[]) => Array.isArray(value) ? value.join(",") : value;

/**
 * The email subject of a notification, derived from its points. Accepts the
 * notification type as a string (compose) or a number (EFASNotification).
 */
export function buildNotificationSubject(notificationType: string | number, points: SubjectPoint[]): string {
    const type = String(notificationType);
    const country = asText(points[0].Country);

    if (type === '3') {
        const uniqueAd2Names = Array.from(new Set(points.map((point) => asText(point.ad2_name))));
        return `EFAS Flash Flood Notification for ${country} - ${uniqueAd2Names.join(",")} ${uniqueAd2Names.length > 1 ? 'Regions' : 'Region'}`;
    }

    return type === '2' ?
        `EFAS Flood Notification for ${country} - River: ${points[0].RiverName} - Type: Informal*`
        : `EFAS Flood Notification for ${country} - River: ${points[0].RiverName} - Type: Formal*`;
}

/**
 * Send the notification body only to the logged-in forecaster, so they can check
 * how it renders in a mail client. Toasts the outcome and reports whether it worked.
 */
export async function sendTestEmailToSelf(subject: string, body: string, userEmail: string): Promise<boolean> {
    const response = await sendNotificationEmail({
        from: EFAS_SELFTEST_FROM,
        subject: subject,
        to: userEmail,
        cc: "",
        body: body,
    });
    const json = await response.json();

    if (response.ok) {
        toast.success(`Test notification sent successfully - ${json.message}`);
        return true;
    }

    toast.error("Encountered an error while sending the test notification");
    // TODO this should report the error to the backend somehow!
    console.error('Error sending test notification:', json);
    return false;
}
